/*******************************************************************************
 * Copyright (c) 2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  PaginationInfo,
  PaginationInfoApiResponse,
  paginationInfoFromApiResponse,
} from '@shared/models/pagination-info';

export interface IamAccount {
  accountId: string;
  resourceName: string;
  name: string;
  rootUserId: string;
  orgId: string;
  status: IamAccountStatus;
  termsConditionsAcceptance: boolean;
  ndaSigned: boolean;
  needsNda: boolean;
  createdAt: string;
  updatedAt: string;
  teamSize?: number;
  product: string;
}

/**
 * This interface describes how the UserApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface IamAccountApiResponse {
  account_id: string;
  resource_name: string;
  name: string;
  root_user_id: string;
  org_id: string;
  status: IamAccountStatus;
  terms_conditions_acceptance: boolean;
  nda_signed: boolean;
  needs_nda: boolean;
  created_at: string;
  updated_at: string;
  team_size?: number;
  product: string;
}

// Enum of IAM accounts statuses
export enum IamAccountStatus {
  inactive = 'ACCOUNT_STATUS_INACTIVE',
  active = 'ACCOUNT_STATUS_ACTIVE',
  suspended = 'ACCOUNT_STATUS_SUSPENDED',
  pending = 'ACCOUNT_STATUS_PENDING',
}

/**
 * This method transforms the UserApiResponse into our own
 * User object which our application understands
 * @param response the pagination API response
 * @returns the deserialized User
 */
export const iamAccountFromApiResponse = (response: IamAccountApiResponse): IamAccount => {
  return {
    accountId: response.account_id,
    resourceName: response.resource_name,
    name: response.name,
    rootUserId: response.root_user_id,
    orgId: response.org_id,
    status: response.status,
    termsConditionsAcceptance: response.terms_conditions_acceptance,
    ndaSigned: response.nda_signed,
    needsNda: response.needs_nda,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    ...(response.team_size && {teamSize: response.team_size}),
    product: response.product,
  };
};

// List IAM accounts

export interface IamAccountsPagination {
  iamAccounts: IamAccount[];
  pagination: PaginationInfo;
}

/**
 * This interface describes how the IamAccountsPaginationApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface IamAccountsPaginationApiResponse {
  accounts: Array<IamAccountApiResponse>;
  pagination_info: PaginationInfoApiResponse;
}

/**
 * This method transforms the IamAccountsPaginationApiResponse into our own
 * IamAccountsPagination object which our application understands
 * @param response the pagination API response
 * @returns the deserialized IamAccountPagination
 */
export const iamAccountsPaginationFromResponse = (
  response: IamAccountsPaginationApiResponse
): IamAccountsPagination => {
  return {
    iamAccounts: response.accounts.map((iamAccountResponse: IamAccountApiResponse) =>
      iamAccountFromApiResponse(iamAccountResponse)
    ),
    pagination: paginationInfoFromApiResponse(response.pagination_info),
  };
};
// List IAM accounts names

export interface ListIamAccountsNames {
  accountNames: IamAccountsNames[];
}

export interface ListIamAccountsNamesApiResponse {
  account_names: IamAccountsNamesApiResponse[];
}

export const listIamAccountsNamesFromApiResponse = (
  response: ListIamAccountsNamesApiResponse
): ListIamAccountsNames => {
  return {
    accountNames: response.account_names.map(account =>
      iamAccountsNamesFromApiResponse(account)
    ),
  };
};

export interface IamAccountsNames {
  accountId: string;
  accountName: string;
}

export interface IamAccountsNamesApiResponse {
  account_id: string;
  account_name: string;
}

export const iamAccountsNamesFromApiResponse = (
  response: IamAccountsNamesApiResponse
): IamAccountsNames => {
  return {
    accountId: response.account_id,
    accountName: response.account_name,
  };
};
